import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { achievementsAPI } from '../services/api';
import { positionsAPI } from '../services/positionsAPI';
import { Achievement, Position } from '../types';
import { getErrorMessage } from '../utils/errorUtils';
import PageHeader from '../components/PageHeader';
import DataTable, { Column } from '../components/DataTable';
import SidePanel from '../components/SidePanel';
import { API_BASE } from '../utils/fileUtils';
import '../styles/global.css';

const INITIAL_FORM = { title: '', description: '', required_percentage: 100 };

const AdminAchievements: React.FC = () => {
  const { user: currentUser } = useAuth();
  const [achievements, setAchievements] = useState<Achievement[]>([]);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [positions, setPositions] = useState<Position[]>([]);
  const [achievementPositions] = useState<Map<number, Position[]>>(new Map());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editingAchievement, setEditingAchievement] = useState<Achievement | null>(null);
  const [editAchIndex, setEditAchIndex] = useState(-1);
  const [formData, setFormData] = useState(INITIAL_FORM);

  useEffect(() => { loadAchievements(); loadPositions(); }, []);

  const loadPositions = async () => {
    try { setPositions(await positionsAPI.getPositions()); }
    catch (err) { console.error(err); }
  };

  const loadAchievements = async () => {
    try {
      setLoading(true);
      setAchievements(await achievementsAPI.getAchievements());
    } catch (err: any) {
      setError(getErrorMessage(err) || 'Ошибка загрузки достижений');
    } finally { setLoading(false); }
  };

  const openCreate = () => { setEditingAchievement(null); setFormData(INITIAL_FORM); setShowForm(true); };

  const openEdit = (a: Achievement) => {
    const idx = achievements.findIndex(x => x.id === a.id);
    setEditingAchievement(a);
    setEditAchIndex(idx);
    setFormData({ title: a.title, description: a.description, required_percentage: a.required_percentage });
    setShowForm(true);
  };

  const closeForm = () => {
    setShowForm(false);
    setEditingAchievement(null);
    setEditAchIndex(-1);
    setFormData(INITIAL_FORM);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const data = { ...formData, required_percentage: 100 };
      if (editingAchievement) {
        await achievementsAPI.updateAchievement(editingAchievement.id, data);
      } else {
        await achievementsAPI.createAchievement(data);
      }
      closeForm(); loadAchievements();
    } catch (err: any) { setError(getErrorMessage(err) || 'Ошибка сохранения'); }
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm('Удалить достижение?')) return;
    try { await achievementsAPI.deleteAchievement(id); loadAchievements(); }
    catch (err: any) { setError(getErrorMessage(err) || 'Ошибка удаления'); }
  };

  const columns: Column<Achievement>[] = [
    {
      key: 'title', header: 'Достижение',
      render: (a) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
          {a.icon_path ? (
            <img src={`${API_BASE}/uploads/${a.icon_path}`} alt={a.title} style={{ width: 36, height: 36, objectFit: 'cover', borderRadius: 4, flexShrink: 0 }} />
          ) : (
            <div style={{ width: 36, height: 36, background: 'var(--primary-orange)', borderRadius: 4, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white', fontWeight: 'bold', fontSize: 14, flexShrink: 0 }}>A</div>
          )}
          <strong style={{ color: 'var(--primary-orange)' }}>{a.title}</strong>
        </div>
      ),
    },
    {
      key: 'description', header: 'Описание',
      render: (a) => (
        <span title={a.description} className="cell-truncate" style={{ maxWidth: 320, display: 'block' }}>
          {a.description}
        </span>
      ),
    },
    {
      key: 'required_percentage', header: 'Требуется',
      width: '110px',
      render: (a) => <span className="status-badge status-approved">{a.required_percentage}%</span>,
    },
  ];

  const renderExpandedAchievement = (a: Achievement) => (
    <div style={{ display: 'flex', gap: '2rem', alignItems: 'flex-start', flexWrap: 'wrap' }}>
      <div className="exp-grid" style={{ flex: 1 }}>
        <div style={{ gridColumn: '1 / -1' }}>
          <div className="exp-label">Полное описание</div>
          <div className="exp-value">{a.description}</div>
        </div>
        <div>
          <div className="exp-label">Создано</div>
          <div className="exp-value">{new Date(a.created_at).toLocaleDateString('ru-RU')}</div>
        </div>
        <div>
          <div className="exp-label">ID</div>
          <div className="exp-value">#{a.id}</div>
        </div>
      </div>
      <div className="exp-actions" onClick={(e) => e.stopPropagation()}>
        <button className="btn btn-primary btn-sm" onClick={() => openEdit(a)}>Правка</button>
        <button className="btn btn-danger btn-sm" onClick={() => handleDelete(a.id)}>Удалить</button>
      </div>
    </div>
  );

  if (currentUser?.role !== 'admin')
    return <div className="container"><div className="error">Нет прав доступа</div></div>;

  if (loading) return <div className="loading">Загрузка...</div>;

  return (
    <div className="container" style={{ paddingTop:'1.5rem', paddingBottom:'2rem' }}>
      <PageHeader
        title="Управление достижениями"
        actions={<button className="btn btn-primary" onClick={openCreate}>+ Создать достижение</button>}
      />
      {error && <div className="error">{error}</div>}

      <DataTable
        columns={columns}
        data={achievements}
        rowKey={(a) => a.id}
        emptyText="Нет достижений. Создайте первое!"
        minWidth="420px"
        renderExpanded={renderExpandedAchievement}
      />

      <SidePanel
        isOpen={showForm}
        onClose={closeForm}
        title={editingAchievement ? 'Редактировать достижение' : 'Новое достижение'}
        onPrev={editingAchievement ? () => openEdit(achievements[editAchIndex - 1]) : undefined}
        onNext={editingAchievement ? () => openEdit(achievements[editAchIndex + 1]) : undefined}
        hasPrev={editAchIndex > 0}
        hasNext={editAchIndex < achievements.length - 1}
        navLabel={editingAchievement ? `${editAchIndex + 1} из ${achievements.length}` : undefined}
      >
        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label className="form-label">Название</label>
            <input type="text" className="form-control" value={formData.title} onChange={(e) => setFormData({...formData, title:e.target.value})} placeholder="Название достижения" required />
          </div>
          <div className="form-group">
            <label className="form-label">Описание</label>
            <textarea className="form-control" rows={5} value={formData.description} onChange={(e) => setFormData({...formData, description:e.target.value})} placeholder="Опишите, что нужно сделать" required />
          </div>
          <div className="section-card" style={{ padding:'0.875rem' }}>
            <p style={{ margin:0, color:'var(--text-muted)', fontSize:'0.82rem' }}>
              Привязку достижений к должностям выполняйте на странице «Должности».
            </p>
          </div>
          <div className="side-panel__footer">
            <button type="button" className="btn btn-secondary" onClick={closeForm}>Отмена</button>
            <button type="submit" className="btn btn-primary">{editingAchievement ? 'Сохранить' : 'Создать'}</button>
          </div>
        </form>
      </SidePanel>
    </div>
  );
};

export default AdminAchievements;
